import {
  MANAGE_PAGE_HREF,
  manageLayoutFor,
  type ManageGroup,
  type ManageGroupId,
  type ManageMain,
  type ManagePageId,
  type Me,
} from '@myrewards/contracts';
import { itemFromState } from '../app/nav';
import type { LevelTwoGroups, LevelTwoItem } from '../shell/LevelTwoTabs';

// APP-234 (Tom, 2026-10-28, option B "Manage"): one top-level menu for the work a person does for
// others, its pages grouped by role. APP-236 (Tom, 2026-10-29, option C): the groups are a row of
// pills, main work first, above the current group's pages, and the Executive pages are its
// Executive group. The server decides which groups and pages, and their order (`me.manage`,
// `me.manageMain`, `manageLayoutFor`); this file gives each its words and its address.

/** Manage's own address: it opens on the person's first tab. */
export const MANAGE_HREF = '/manage';

/** Each Manage page's tab: its words, and its address from the one table (APP-255). */
export const MANAGE_PAGE: Readonly<Record<ManagePageId, { label: string; href: string }>> = {
  'spot-review': { label: 'Review', href: MANAGE_PAGE_HREF['spot-review'] },
  'spot-all': { label: 'Spot awards', href: MANAGE_PAGE_HREF['spot-all'] },
  // APP-249: no group offers it as a tab now (the committee reads letters from Spot awards, the
  // Stock Admin team checks them from its queue); the page keeps its address.
  'spot-letters': { label: 'Spot letters', href: MANAGE_PAGE_HREF['spot-letters'] },
  // D2b: never "Earning statements" here, which is the Stock Admin team's page, not this one.
  'spot-statements': {
    label: 'Spot earning statements',
    href: MANAGE_PAGE_HREF['spot-statements'],
  },
  'spot-decisions': { label: 'Spot decisions', href: MANAGE_PAGE_HREF['spot-decisions'] },
  queue: { label: 'Queue', href: MANAGE_PAGE_HREF.queue },
  letters: { label: 'Letters', href: MANAGE_PAGE_HREF.letters },
  'earning-statements': {
    label: 'Earning statements',
    href: MANAGE_PAGE_HREF['earning-statements'],
  },
  shareworks: { label: 'Shareworks', href: MANAGE_PAGE_HREF.shareworks },
  payroll: { label: 'Payroll queue', href: MANAGE_PAGE_HREF.payroll },
  // APP-236: the Executive group's pages, under Manage (they were `/executive/…`, which redirect).
  'budget-pools': { label: 'Budget pools', href: MANAGE_PAGE_HREF['budget-pools'] },
  values: { label: 'Values', href: MANAGE_PAGE_HREF.values },
  'spot-funding': { label: 'Spot funding', href: MANAGE_PAGE_HREF['spot-funding'] },
  // The Executive group's page and a Sector Lead's or approver's, with its one address (D5).
  approvals: { label: 'Approvals', href: MANAGE_PAGE_HREF.approvals },
  // APP-245 D1: a letter holder's page keeps its one address, the account menu's and the holder
  // notices' link (APP-238 D4), as Approvals keeps its own.
  'letters-to-hand-out': {
    label: 'Letters to hand out',
    href: MANAGE_PAGE_HREF['letters-to-hand-out'],
  },
};

/** Each group's name on its pill: the role its pages are for, whoever reads them (D2c). */
export const MANAGE_GROUP_LABEL: Readonly<Record<ManageGroupId, string>> = {
  committee: 'Committee',
  team: 'My team',
  stock_admin: 'Stock Admin',
  payroll: 'Payroll',
  executive: 'Executive',
  sector_lead: 'Sector Lead',
  approver: 'PAA/TAA approver',
};

/**
 * The Spot work tabs' addresses under Manage (`/manage/spot/:section`), by the Spot page's own tab
 * ids, and the old `?tab=` each replaces under Recognize › Spot.
 */
export const MANAGE_SPOT_SECTION = {
  review: 'review',
  all: 'all',
  decisions: 'supervision',
  funding: 'funding',
  letters: 'fulfillment',
  statements: 'statements',
} as const;
export type ManageSpotSection = keyof typeof MANAGE_SPOT_SECTION;
export type ManageSpotTab = (typeof MANAGE_SPOT_SECTION)[ManageSpotSection];

/** The section for a Spot work tab: `/manage/spot/<section>`. */
export const SPOT_TAB_SECTION: Readonly<Record<ManageSpotTab, ManageSpotSection>> = {
  review: 'review',
  all: 'all',
  supervision: 'decisions',
  funding: 'funding',
  fulfillment: 'letters',
  statements: 'statements',
};

/** The Manage page each Spot work tab is. */
export const SPOT_TAB_PAGE: Readonly<Record<ManageSpotTab, ManagePageId>> = {
  review: 'spot-review',
  all: 'spot-all',
  supervision: 'spot-decisions',
  funding: 'spot-funding',
  fulfillment: 'spot-letters',
  statements: 'spot-statements',
};

export function isManageSpotSection(value: string | undefined): value is ManageSpotSection {
  return value !== undefined && Object.hasOwn(MANAGE_SPOT_SECTION, value);
}

/**
 * The address of a Spot work tab under Manage, with its own query (`?stage=committee`, `?award=…`).
 * Every link to Spot work goes through here, so none still says `/recognize/spot?tab=…`.
 */
export function spotWorkHref(
  tab: ManageSpotTab,
  query?: Readonly<Record<string, string>> | URLSearchParams,
): string {
  const params = query instanceof URLSearchParams ? query : new URLSearchParams(query);
  const search = params.toString();
  return `/manage/spot/${SPOT_TAB_SECTION[tab]}${search === '' ? '' : `?${search}`}`;
}

/**
 * APP-235 (Tom, option A): where a supervisor is appointed. Spot awards at "No manager set" is the
 * whole list of colleagues waiting for one; naming a colleague (`nomination`, `person`) opens the
 * appoint form for them, as the Home to-do does. Spot awards checks the pair against what the
 * reader may appoint before it opens anything.
 */
export function spotAppointHref(colleague?: { nominationId: string; personId: string }): string {
  return spotWorkHref('all', {
    stage: 'no_manager',
    ...(colleague === undefined
      ? {}
      : { nomination: colleague.nominationId, person: colleague.personId }),
  });
}

/**
 * The old Supervisor appointments tab's addresses (`/manage/spot/appointments`,
 * `/recognize/spot?tab=assignments`) land on Spot awards at "No manager set", keeping the rest of
 * their query (`nomination`, `person` open the form there). The path is fixed and only query
 * parameters are carried, so an address can't send anyone elsewhere.
 */
export function appointmentsMoved(query: URLSearchParams, hash = ''): string {
  const rest = new URLSearchParams(query);
  rest.delete('tab');
  rest.set('stage', 'no_manager');
  return `${spotWorkHref('all', rest)}${hash}`;
}

/**
 * Manage's groups for this person in display order, main work first: the server's answer, or,
 * from an older server, the same rule on the roles alone (as a sample preview has it). Never
 * reordered here.
 */
export function manageGroupsOf(me: Pick<Me, 'roles' | 'manage'>): readonly ManageGroup[] {
  return me.manage ?? manageLayoutFor(me.roles.map((r) => r.role)).groups;
}

/** The person's main work (APP-236 §4), as the server said it; null without Manage. */
export function manageMainOf(me: Pick<Me, 'roles' | 'manage' | 'manageMain'>): ManageMain | null {
  if (me.manageMain !== undefined) return me.manageMain;
  if (me.manage !== undefined) {
    const first = me.manage[0]?.id;
    return first === undefined
      ? null
      : { id: first, automatic: first, choices: me.manage.map((g) => g.id) };
  }
  return manageLayoutFor(me.roles.map((r) => r.role)).main;
}

/** Whether this page is one of the person's Manage pages. */
export function hasManagePage(me: Pick<Me, 'roles' | 'manage'>, page: ManagePageId): boolean {
  return manageGroupsOf(me).some((g) => g.pages.includes(page));
}

/** Where the header's Manage goes: the first page of the person's main work, or Manage itself. */
export function manageHref(me: Pick<Me, 'roles' | 'manage'>): string {
  const first = manageGroupsOf(me)[0]?.pages[0];
  return first === undefined ? MANAGE_HREF : MANAGE_PAGE[first].href;
}

/**
 * The Manage page a Manage address is (the queue's views are the Queue page), or null for another
 * address. For a page drawn without its own component: a preview's sample.
 */
export function managePageAt(pathname: string): ManagePageId | null {
  const path = pathname.replace(/\/+$/, '');
  for (const [id, page] of Object.entries(MANAGE_PAGE) as [ManagePageId, { href: string }][])
    if (page.href === path) return id;
  return path.startsWith(`${MANAGE_PAGE.queue.href}/`) ? 'queue' : null;
}

/** Manage's two rows for one of its pages: the groups' pills, and the current group's pages. */
export interface ManageBar {
  /** Row 1 (`groups.items` has two or more) and which pill is current. */
  groups: LevelTwoGroups;
  /** Row 2: the current group's pages. */
  items: LevelTwoItem[];
  /** The current group's name, which names row 2 ("Committee pages"). */
  groupLabel: string;
  /** The page marked current in row 2, or null. */
  current: ManagePageId | null;
}

/** What waits on the person, by Manage page and group (`manageWaiting`); none by default. */
export interface ManageCounts {
  pages: Partial<Record<ManagePageId, number>>;
  groups: Partial<Record<ManageGroupId, number>>;
}

const NO_COUNTS: ManageCounts = { pages: {}, groups: {} };

/** A count for a pill or a tab: read "<n> waiting", the gold chip; nothing for 0. */
function waitingCount(
  n: number | undefined,
): Pick<LevelTwoItem, 'count' | 'countName' | 'countTone'> {
  return n !== undefined && n > 0 ? { count: n, countName: 'waiting', countTone: 'waiting' } : {};
}

/**
 * APP-236 §2: Manage's bar on a page. `wanted` is the page this is, or the pages it could be (a
 * supervisor's decision is Spot decisions or Spot awards): the first the person has is current,
 * and its group is the current pill. A page shows once, so it's in one group. On a page that isn't
 * one of theirs, the main work group shows with no page marked.
 *
 * Step 2 (§5): each pill shows its group's waiting total, the current group's too, and each page
 * its own, except the page this is. Never a 0.
 */
export function manageBarFor(
  groups: readonly ManageGroup[],
  main: ManageGroupId | null,
  wanted: readonly ManagePageId[],
  counts: ManageCounts = NO_COUNTS,
): ManageBar | null {
  const current = wanted.find((id) => groups.some((g) => g.pages.includes(id))) ?? null;
  const group =
    (current === null ? undefined : groups.find((g) => g.pages.includes(current))) ?? groups[0];
  if (group === undefined) return null;
  return {
    groups: {
      label: 'Manage groups',
      items: groups.map((g) => {
        const first = g.pages[0];
        return {
          id: g.id,
          label: MANAGE_GROUP_LABEL[g.id],
          href: first === undefined ? MANAGE_HREF : MANAGE_PAGE[first].href,
          ...waitingCount(counts.groups[g.id]),
        };
      }),
      current: group.id,
      ...(main !== null && { dividerAfter: main }),
    },
    items: group.pages.map((page) => ({
      id: page,
      label: MANAGE_PAGE[page].label,
      href: MANAGE_PAGE[page].href,
      // Never on the page you're on: you're looking at it.
      ...(page === current ? {} : waitingCount(counts.pages[page])),
    })),
    groupLabel: MANAGE_GROUP_LABEL[group.id],
    current,
  };
}

/**
 * Where a Spot nomination's own page (`/recognize/spot/:id`) belongs (D5b): the author's, the
 * nominee's and the committee's review share the address. Opened from a page that said where it
 * belongs (a Manage tab, My nominations), there; otherwise under Manage for a committee member,
 * whose review it is, and under Recognize for everyone else.
 */
export function spotRecordArea(
  state: unknown,
  me: Pick<Me, 'roles' | 'manage' | 'nav'>,
): 'manage' | 'recognize' {
  const said = itemFromState(state, me);
  if (said === 'manage' || said === 'recognize') return said;
  return me.nav.includes('manage') && hasManagePage(me, 'spot-review') ? 'manage' : 'recognize';
}
